import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { RegisterLink } from "@kinde-oss/kinde-auth-react/components";
import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ImageSelectItem, Loading, Panel, Typography } from "@/components/atoms";
import { Restricted } from "@/components/pages";
import { useOperator, useProviderCode, useSiteConfig } from "@/hooks";
// import { useSignUpContext } from "../context";
import { userTypeImage, userTypes } from "../fixtures";
import { UserType } from "../types";

export const UserSelectForm = () => {
  const { register } = useKindeAuth();
  const operator = useOperator();
  const { name, providerCode: activeOperatorCode } = operator;
  const allowedUserTypes: UserType[] = ["passenger", "executive_assistant", "travel_manager"];

  // Show only allowed user types
  const options = allowedUserTypes.map((type) => userTypes[type]).filter(Boolean); // Remove any undefined entries

  const urlProviderCode = useProviderCode();
  const { domain } = useSiteConfig();
  // Use URL provider code first, then active operator's provider code, then domain as last resort
  const code = urlProviderCode || activeOperatorCode || domain;

  // Forward an invited email carried on the signup URL (`?email=`) to Kinde as `loginHint` so the
  // hosted signup page pre-fills it (editable).
  const [searchParams] = useSearchParams();
  const invitedEmail = searchParams.get("email")?.trim() || undefined;
  // Present only on EA invites (operator invites omit it): locks the persona so we skip the chooser.
  const lockedTypeParam = searchParams.get("type")?.trim();
  const lockedType: UserType | undefined =
    lockedTypeParam && (allowedUserTypes as string[]).includes(lockedTypeParam) ? (lockedTypeParam as UserType) : undefined;

  const buildRegisterProps = useCallback(
    (type: UserType) => ({ utm_campaign: code, utm_source: "web-app", utm_content: "client", utm_medium: type }),
    [code]
  );

  // An EA-invited non-user is ALWAYS a Client, so the EA invite CTA locks the persona (`?type=`):
  // skip the chooser and fire that Kinde register straight away. Operator invites carry only
  // `?email=` (no lock), so they still see the chooser — with the email prefilled.
  const autoRegistered = useRef(false);
  useEffect(() => {
    if (!invitedEmail || !lockedType || autoRegistered.current) return;
    autoRegistered.current = true;
    register({ loginHint: invitedEmail, properties: buildRegisterProps(lockedType) });
  }, [invitedEmail, lockedType, buildRegisterProps, register]);

  if (options.length === 0) return <Restricted />;
  // Locked invite → redirecting straight to Kinde; don't flash the chooser.
  if (invitedEmail && lockedType) return <Loading />;

  return (
    <div>
      <Panel>
        <div className="mb-7 flex flex-col gap-y-1.5">
          <Typography variant="h1">Sign up</Typography>
        </div>
        <div className="flex flex-col gap-y-4">
          <Typography variant="title">{`How are you primarily using ${name}?`}</Typography>
          <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 md:gap-5 lg:grid-cols-5">
            {options.map((option) => (
              <RegisterLink key={option.type} loginHint={invitedEmail} properties={buildRegisterProps(option.type)}>
                <ImageSelectItem src={userTypeImage[option.type]} label={option.name} />
              </RegisterLink>
            ))}
          </div>
        </div>
      </Panel>
    </div>
  );
};
